import React from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import { ArrowRight, MapPin, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const RiversidePage = () => {
  const scrollToContact = () => {
    window.location.href = '/quote';
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Riverside CA 92501 | County Seat Legal Services | Attorney Support"
        description="Professional process server in Riverside, CA 92501. County courthouse specialists serving attorneys & law firms. Same-day court filing, skip tracing, document retrieval. Licensed & bonded. Request a quote online."
        keywords="process server Riverside CA 92501, Riverside County Superior Court, downtown Riverside process serving, county courthouse legal services, attorney service Riverside, court filing 92501, legal document service Riverside"
        canonical="/cities/riverside"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ marginBottom: '2rem' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              lineHeight: '1.1'
            }}>
              Process Server Riverside, California
            </h1>

            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional legal document services in Riverside, CA 92501-92509. Expert, registered and bonded process servers serving the county seat, downtown historic district, UC Riverside area, and Canyon Crest neighborhoods. We specialize in courthouse proximity services, government office filings, and university community legal needs.
            </p>
            
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid var(--border-light)',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <h3 style={{ color: 'var(--brand-primary)', marginBottom: '1rem', fontSize: '1.1rem' }}>
                🏛️ Riverside County Courthouse & Government Services
              </h3>
              <p style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                <strong>Riverside County Superior Court - Main Street</strong><br />
                4050 Main St, Riverside, CA 92501 (Downtown location - walking distance)
              </p>
              <p style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                <strong>Riverside Historic Courthouse</strong><br />
                4050 Main St, Riverside, CA 92501 (Civil and family law matters)
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Primary courthouse for Riverside County. We provide same-day courthouse filing and process serving with courthouse proximity advantage.
              </p>
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '3rem'
            }}>
              <button 
                onClick={scrollToContact}
                className="btn-primary"
                style={{ fontSize: '1.1rem', padding: '1rem 2rem' }}
              >
                Get Free Quote
              </button>
              <a href="mailto:support@legaldocspecialist.com"
                className="btn-secondary"
                style={{ 
                  fontSize: '1.1rem', 
                  padding: '1rem 2rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Riverside Legal Document Services
            </h2>
            <p className="body-medium" style={{ 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Serving Riverside and the greater Inland Empire with comprehensive legal support services.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem',
            marginBottom: '3rem'
          }}>
            {/* Professional Services */}
            <div className="service-card">
              <div style={{
                backgroundColor: 'var(--brand-primary)',
                borderRadius: '12px',
                padding: '1.5rem',
                color: 'white',
                marginBottom: '1rem'
              }}>
                <CheckCircle size={32} style={{ marginBottom: '1rem' }} />
                <h3 className="heading-3" style={{ color: 'white', marginBottom: '1rem' }}>
                  Professional Services
                </h3>
              </div>

              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>County Courthouse Specialists</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Main Street courthouse, civil and family divisions, probate court experience</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>UC Riverside Area</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>University community, student services, Canyon Crest and nearby residential areas</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Downtown Business District</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Main Street corridor, government offices, historic district, professional buildings</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Government & Municipal Services</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>City Hall, county offices, administrative buildings. ZIP codes 92501, 92502, 92503, 92504, 92505, 92506, 92507, 92508, 92509</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Deep knowledge of Riverside County government procedures, courthouse protocols, and local business community requirements.
                </p>
              </div>
            </div>

            {/* Riverside Advantages */}
            <div className="service-card">
              <div style={{
                backgroundColor: 'var(--accent-blue)',
                borderRadius: '12px',
                padding: '1.5rem',
                color: 'white',
                marginBottom: '1rem'
              }}>
                <MapPin size={32} style={{ marginBottom: '1rem' }} />
                <h3 className="heading-3" style={{ color: 'white', marginBottom: '1rem' }}>
                  Riverside Advantages
                </h3>
              </div>

              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                color: 'var(--text-primary)',
                lineHeight: '1.8'
              }}>
                <li style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--brand-primary)', minWidth: '20px' }}>✓</span>
                  <span><strong>County Seat Advantage:</strong> Direct courthouse access and county government proximity</span>
                </li>
                <li style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--brand-primary)', minWidth: '20px' }}>✓</span>
                  <span><strong>Historic District Knowledge:</strong> Familiar with downtown area, Mission Inn vicinity, and heritage square</span>
                </li>
                <li style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--brand-primary)', minWidth: '20px' }}>✓</span>
                  <span><strong>University Community:</strong> UCR campus area, student housing, and academic district expertise</span>
                </li>
                <li style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--brand-primary)', minWidth: '20px' }}>✓</span>
                  <span><strong>Government Relations:</strong> Established relationships with county offices and municipal departments</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--brand-primary)', minWidth: '20px' }}>✓</span>
                  <span><strong>Inland Empire Hub:</strong> Strategic location for serving broader Inland Empire region</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact CTA */}
          <div style={{
            textAlign: 'center',
            backgroundColor: 'var(--bg-page)',
            padding: '3rem 2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
              Ready to Get Started?
            </h3>
            <p className="body-medium" style={{ 
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              maxWidth: '500px',
              margin: '0 auto 2rem auto'
            }}>
              Contact us today for professional process serving and legal document services in Riverside, California.
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Link 
                to="/place-order"
                className="btn-primary"
                style={{ textDecoration: 'none' }}
              >
                Place Order Now
              </Link>
              <button 
                onClick={scrollToContact}
                className="btn-secondary"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @media (max-width: 767px) {
          .service-card {
            min-width: 300px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default RiversidePage;